import React, { useState } from 'react';
import { generateDeck, calculateWinRates, getCardDisplay } from '../services/pokerCalculator';
import './DataCalculation.css';

function PokerCalculator() {
  const deck = generateDeck();

  const [players, setPlayers] = useState([
    { id: 1, cards: [] },
    { id: 2, cards: [] }
  ]);
  const [communityCards, setCommunityCards] = useState([]);
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('calculator'); // 'calculator' or 'instructions'

  // Modal state
  const [showCardSelector, setShowCardSelector] = useState(false);
  const [selectingFor, setSelectingFor] = useState(null); // { type: 'player', playerId } or { type: 'community' }
  const [selectedSuit, setSelectedSuit] = useState(null);

  // Get all used cards
  const getUsedCards = () => {
    const used = [...communityCards];
    players.forEach(player => {
      used.push(...player.cards);
    });
    return used;
  };

  // Check if card is used
  const isCardUsed = (card) => {
    return getUsedCards().some(used =>
      used.rank === card.rank && used.suit === card.suit
    );
  };

  // Open card selector
  const openCardSelector = (type, playerId = null) => {
    setSelectingFor({ type, playerId });
    setSelectedSuit(null);
    setShowCardSelector(true);
  };

  // Close card selector
  const closeCardSelector = () => {
    setShowCardSelector(false);
    setSelectingFor(null);
    setSelectedSuit(null);
  };

  // Handle card selection
  const handleCardSelect = (card) => {
    if (isCardUsed(card)) {
      alert('此牌已被使用');
      return;
    }

    if (selectingFor.type === 'community') {
      if (communityCards.length < 4) {
        setCommunityCards([...communityCards, card]);
        closeCardSelector();
      }
    } else if (selectingFor.type === 'player') {
      setPlayers(players.map(p => {
        if (p.id === selectingFor.playerId && p.cards.length < 2) {
          return { ...p, cards: [...p.cards, card] };
        }
        return p;
      }));

      // Close if player has 2 cards
      const player = players.find(p => p.id === selectingFor.playerId);
      if (player && player.cards.length >= 1) {
        closeCardSelector();
      }
    }
  };

  // Remove card
  const handleRemoveCard = (type, playerId, cardIndex) => {
    if (type === 'community') {
      setCommunityCards(communityCards.filter((_, idx) => idx !== cardIndex));
    } else {
      setPlayers(players.map(p => {
        if (p.id === playerId) {
          return { ...p, cards: p.cards.filter((_, idx) => idx !== cardIndex) };
        }
        return p;
      }));
    }
  };

  // Add player
  const handleAddPlayer = () => {
    if (players.length < 10) {
      setPlayers([...players, { id: players.length + 1, cards: [] }]);
    }
  };

  // Remove player
  const handleRemovePlayer = (playerId) => {
    if (players.length > 2) {
      setPlayers(players.filter(p => p.id !== playerId));
    }
  };

  // Calculate win rates
  const handleCalculate = async () => {
    const validPlayers = players.filter(p => p.cards.length > 0);
    if (validPlayers.length < 2 && communityCards.length === 0) {
      alert('請至少為 2 位玩家選擇手牌，或選擇公牌');
      return;
    }

    setCalculating(true);
    setResults(null);

    try {
      setTimeout(() => {
        const winRates = calculateWinRates(validPlayers, communityCards, 10000);
        setResults(winRates);
        setCalculating(false);
      }, 100);
    } catch (error) {
      console.error('Calculation error:', error);
      alert('計算錯誤：' + error.message);
      setCalculating(false);
    }
  };

  // Reset all
  const handleReset = () => {
    setPlayers([
      { id: 1, cards: [] },
      { id: 2, cards: [] }
    ]);
    setCommunityCards([]);
    setResults(null);
    setShowCardSelector(false);
    setSelectingFor(null);
    setSelectedSuit(null);
  };

  // Get available ranks for selected suit
  const getAvailableRanks = () => {
    if (!selectedSuit) return [];

    return ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'].filter(rank => {
      const card = deck.find(c => c.rank === rank && c.suit === selectedSuit);
      return !isCardUsed(card);
    });
  };

  return (
    <div className="poker-calculator">
      <div className="page-header">
        <div>
          <h1>勝率計算機</h1>
          <p>選擇手牌和公牌，計算各玩家勝率</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          計算機
        </button>
        <button
          className={`tab-button ${activeTab === 'instructions' ? 'active' : ''}`}
          onClick={() => setActiveTab('instructions')}
        >
          使用說明
        </button>
      </div>

      {/* Calculator Tab */}
      {activeTab === 'calculator' && (
        <>
          <div className="poker-container">
        {/* Left Panel - Players */}
        <div className="players-panel">
          <div className="panel-header">
            <h2>玩家手牌</h2>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAddPlayer}
              disabled={players.length >= 10}
            >
              + 新增玩家
            </button>
          </div>

          <div className="players-list">
            {players.map((player, idx) => (
              <div key={player.id} className="player-item">
                <div className="player-header">
                  <span className="player-name">玩家 {idx + 1}</span>
                  {players.length > 2 && (
                    <button
                      className="btn-icon"
                      onClick={() => handleRemovePlayer(player.id)}
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="player-cards">
                  {player.cards.map((card, cardIdx) => (
                    <div
                      key={cardIdx}
                      className="selected-card"
                      onClick={() => handleRemoveCard('player', player.id, cardIdx)}
                      title="點擊移除"
                    >
                      {getCardDisplay(card)}
                    </div>
                  ))}
                  {player.cards.length < 2 && (
                    <div
                      className="empty-card clickable"
                      onClick={() => openCardSelector('player', player.id)}
                    >
                      +
                    </div>
                  )}
                  {player.cards.length < 1 && (
                    <div
                      className="empty-card clickable"
                      onClick={() => openCardSelector('player', player.id)}
                    >
                      +
                    </div>
                  )}
                </div>

                {results && results[idx] && (
                  <div className="player-result">
                    <div className="win-rate">
                      勝率: <strong>{results[idx].winRate}%</strong>
                    </div>
                    <div className="tie-rate">
                      平局: {results[idx].tieRate}%
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Community Cards */}
          <div className="community-section">
            <div className="community-header">
              <h3>公牌 (Flop/Turn)</h3>
              <span className="card-count">{communityCards.length}/4</span>
            </div>

            <div className="community-cards">
              {communityCards.map((card, idx) => (
                <div
                  key={idx}
                  className="selected-card"
                  onClick={() => handleRemoveCard('community', null, idx)}
                  title="點擊移除"
                >
                  {getCardDisplay(card)}
                </div>
              ))}
              {Array.from({ length: Math.max(0, 4 - communityCards.length) }).map((_, idx) => (
                <div
                  key={`empty-${idx}`}
                  className="empty-card clickable"
                  onClick={() => openCardSelector('community')}
                >
                  +
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

          {/* Action Buttons */}
          <div className="calculator-actions">
            <button className="btn btn-secondary" onClick={handleReset}>
              重置
            </button>
            <button
              className="btn btn-success"
              onClick={handleCalculate}
              disabled={calculating}
            >
              {calculating ? '計算中...' : '計算勝率'}
            </button>
          </div>

          {/* Results Section */}
          {results && (
            <div className="results-section">
              {/* Quick Results */}
              <div className="quick-results-panel">
                <h2>快速結果</h2>
                <div className="quick-results-grid">
                  {results.map((result, idx) => (
                    <div key={idx} className="quick-result-card">
                      <div className="player-info">
                        <span className="player-label">玩家 {result.player}</span>
                        <div className="player-cards">
                          {players.find(p => p.id === result.player)?.cards.map((card, cardIdx) => (
                            <span key={cardIdx} className="mini-card">
                              {getCardDisplay(card)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="win-rate-display">
                        <span className="win-rate-number">{result.winRate}%</span>
                        <span className="win-rate-label">勝率</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Detailed Results */}
              <div className="detailed-results-panel">
                <h2>詳細計算結果</h2>
          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>玩家</th>
                  <th>手牌</th>
                  <th>勝場</th>
                  <th>平局</th>
                  <th>敗場</th>
                  <th>勝率</th>
                  <th>平局率</th>
                  <th>總勝率</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, idx) => (
                  <tr key={idx}>
                    <td>玩家 {result.player}</td>
                    <td className="hand-cards">
                      {players[idx].cards.map((card, cardIdx) => (
                        <span key={cardIdx} className="result-card">
                          {getCardDisplay(card)}
                        </span>
                      ))}
                      {players[idx].cards.length === 0 && <span className="no-cards">未選擇</span>}
                    </td>
                    <td>{result.wins}</td>
                    <td>{result.ties}</td>
                    <td>{result.losses}</td>
                    <td className="win-rate-cell">{result.winRate}%</td>
                    <td>{result.tieRate}%</td>
                    <td className="total-rate-cell">{result.totalRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

                <div className="calculation-info">
                  <p>* 基於 10,000 次蒙地卡羅模擬計算，誤差約 ±1%</p>
                  <p>* 總勝率 = 勝率 + 平局率</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Instructions Tab */}
      {activeTab === 'instructions' && (
        <div className="instructions-tab">
          <h2>使用說明</h2>

          <div className="instruction-item">
            <div className="instruction-number">1</div>
            <div className="instruction-content">
              <h3>設定玩家</h3>
              <p>預設 2 位玩家，可新增至最多 10 位</p>
            </div>
          </div>

          <div className="instruction-item">
            <div className="instruction-number">2</div>
            <div className="instruction-content">
              <h3>選擇手牌</h3>
              <p>點擊玩家的空白牌位，選擇 2 張手牌</p>
            </div>
          </div>

          <div className="instruction-item">
            <div className="instruction-number">3</div>
            <div className="instruction-content">
              <h3>選擇公牌</h3>
              <p>點擊公牌區的空白牌位，選擇 0-4 張公牌</p>
            </div>
          </div>

          <div className="instruction-item">
            <div className="instruction-number">4</div>
            <div className="instruction-content">
              <h3>計算勝率</h3>
              <p>點擊右上角「計算勝率」按鈕，查看各玩家獲勝機率</p>
            </div>
          </div>

          <div className="calculation-note">
            <h4>計算方式</h4>
            <p>使用蒙地卡羅模擬法進行 10,000 次隨機模擬，計算每位玩家的勝率、平局率和敗率。</p>
            <p>誤差範圍約 ±1%</p>
          </div>

          <div className="poker-hands-guide">
            <h4>德州撲克牌型大小</h4>
            <ol>
              <li><strong>皇家同花順 (Royal Flush)</strong> - A, K, Q, J, 10 同花色</li>
              <li><strong>同花順 (Straight Flush)</strong> - 五張連續數字且同花色</li>
              <li><strong>四條 (Four of a Kind)</strong> - 四張相同數字</li>
              <li><strong>葫蘆 (Full House)</strong> - 三條 + 一對</li>
              <li><strong>同花 (Flush)</strong> - 五張同花色（不連續）</li>
              <li><strong>順子 (Straight)</strong> - 五張連續數字（不同花色）</li>
              <li><strong>三條 (Three of a Kind)</strong> - 三張相同數字</li>
              <li><strong>兩對 (Two Pair)</strong> - 兩個對子</li>
              <li><strong>一對 (One Pair)</strong> - 兩張相同數字</li>
              <li><strong>高牌 (High Card)</strong> - 沒有任何組合</li>
            </ol>
          </div>
        </div>
      )}

      {/* Card Selector Modal */}
      {showCardSelector && (
        <div className="modal-overlay" onClick={closeCardSelector}>
          <div className="modal card-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectingFor?.type === 'community'
                  ? '選擇公牌'
                  : `選擇玩家 ${selectingFor?.playerId} 的手牌 (${
                      players.find(p => p.id === selectingFor?.playerId)?.cards.length + 1 || 1
                    }/2)`
                }
              </h2>
              <button className="close-btn" onClick={closeCardSelector}>×</button>
            </div>

            <div className="modal-body">
              {/* Display selected cards for current selection */}
              {selectingFor && (
                <div className="current-selection-display">
                  <div className="current-selection-label">
                    {selectingFor.type === 'community' ? '已選公牌：' : `玩家 ${selectingFor.playerId} 已選手牌：`}
                  </div>
                  <div className="current-selection-cards">
                    {selectingFor.type === 'community' ? (
                      communityCards.length > 0 ? (
                        communityCards.map((card, idx) => (
                          <span key={idx} className="selected-card-chip">
                            {getCardDisplay(card)}
                          </span>
                        ))
                      ) : (
                        <span className="no-cards-selected">尚未選擇</span>
                      )
                    ) : (
                      (() => {
                        const player = players.find(p => p.id === selectingFor.playerId);
                        return player?.cards.length > 0 ? (
                          player.cards.map((card, idx) => (
                            <span key={idx} className="selected-card-chip">
                              {getCardDisplay(card)}
                            </span>
                          ))
                        ) : (
                          <span className="no-cards-selected">尚未選擇</span>
                        );
                      })()
                    )}
                  </div>
                </div>
              )}

              {!selectedSuit ? (
                // Step 1: Select Suit
                <div className="suit-selector">
                  <h3>請選擇花色</h3>
                  <div className="suit-buttons">
                    {['♠', '♥', '♦', '♣'].map(suit => (
                      <button
                        key={suit}
                        className={`suit-button ${suit === '♥' || suit === '♦' ? 'red' : 'black'}`}
                        onClick={() => setSelectedSuit(suit)}
                      >
                        <span className="suit-icon">{suit}</span>
                        <span className="suit-name">
                          {suit === '♠' && '黑桃'}
                          {suit === '♥' && '紅心'}
                          {suit === '♦' && '方塊'}
                          {suit === '♣' && '梅花'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                // Step 2: Select Rank
                <div className="rank-selector">
                  <div className="selected-suit-display">
                    <button className="back-btn" onClick={() => setSelectedSuit(null)}>
                      ← 重選花色
                    </button>
                    <span className={`current-suit ${selectedSuit === '♥' || selectedSuit === '♦' ? 'red' : 'black'}`}>
                      {selectedSuit}
                    </span>
                  </div>

                  <h3>請選擇點數</h3>
                  <div className="rank-buttons">
                    {getAvailableRanks().map(rank => {
                      const card = deck.find(c => c.rank === rank && c.suit === selectedSuit);
                      return (
                        <button
                          key={rank}
                          className={`rank-button ${selectedSuit === '♥' || selectedSuit === '♦' ? 'red' : 'black'}`}
                          onClick={() => handleCardSelect(card)}
                        >
                          {rank}
                        </button>
                      );
                    })}
                  </div>

                  {getAvailableRanks().length === 0 && (
                    <div className="no-cards-available">
                      此花色的所有牌都已被使用，請選擇其他花色
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PokerCalculator;
